/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { defineMessages } from 'react-intl';

const messages = defineMessages({
  title: {
    id: 'pages.settings.Settings.title',
    defaultMessage: 'Assistant settings'
  },
  subtitle: {
    id: 'pages.settings.Settings.subtitle',
    defaultMessage: 'Customize your preferences across the assistant'
  },
  cancelButtonLabel: {
    id: 'pages.settings.Settings.cancelButtonLabel',
    defaultMessage: 'Cancel'
  },
  saveSettingsLabel: {
    id: 'pages.settings.Settings.saveSettingsLabel',
    defaultMessage: 'save settings'
  },
  saveButtonLabel: {
    id: 'pages.settings.Settings.saveButtonLabel',
    defaultMessage: 'Save'
  },
  savedButtonLabel: {
    id: 'pages.settings.Settings.savedButtonLabel',
    defaultMessage: 'Saved'
  },
  accessControlAndAPIHeader: {
    id: 'pages.settings.Settings.accessControlAndAPIHeader',
    defaultMessage: 'Assistant IDs and API details'
  },
  sslSecurity: {
    id: 'pages.settings.Settings.sslSecurity',
    defaultMessage: 'Security certificates (SSL/TLS)'
  },
  sslSecurityIntro: {
    id: 'pages.settings.Settings.sslSecurityIntro',
    defaultMessage:
      'Upload self-signed or trusted CA certificates to secure the services integrations and extensions in your assistant. To upload multiple certificate files, merge the files to a single file before uploading. Only PEM format is supported. <a>Learn more</a>'
  },
  protocol: {
    id: 'pages.settings.Settings.protocol',
    defaultMessage: 'Protocol'
  },
  sslUploadedSignedCerts: {
    id: 'pages.settings.Settings.sslUploadedSignedCerts',
    defaultMessage:
      'Trust uploaded certificates and any certificates signed by a trusted authority.'
  },
  sslUploadedCerts: {
    id: 'pages.settings.Settings.sslUploadedASignedCerts',
    defaultMessage: 'Trust uploaded certificates only.'
  },
  sslFileTooLarge: {
    id: 'pages.settings.Settings.sslFileTooLarge',
    defaultMessage: 'File is over 500KB.'
  },
  error: {
    id: 'pages.settings.Settings.error',
    defaultMessage: 'Error:'
  },
  sslAllCerts: {
    id: 'pages.settings.Settings.sslAllCerts',
    defaultMessage:
      'Trust all certificates, operation insecure (Not recommended).'
  },
  dragDrop: {
    id: 'pages.settings.Settings.dragDrop',
    defaultMessage: 'Drag and drop files here or click to upload'
  },
  accessControlLink: {
    id: 'pages.settings.Settings.accessControlLink',
    defaultMessage: 'Access control'
  },
  accessControlIntro: {
    id: 'pages.settings.Settings.accessControlDescription',
    defaultMessage:
      'Get IDs and API details for your assistant, skills, and environments.'
  },
  accessControlWoIntro: {
    id: 'pages.settings.Settings.accessControlDescription',
    defaultMessage:
      'View details about the IDs or generate an API key for your assistant, skills, and environments.'
  },
  detailsHeader: {
    id: 'pages.settings.Settings.detailsHeader',
    defaultMessage: 'Details'
  },
  assistantHeader: {
    id: 'pages.settings.Settings.assistantHeader',
    defaultMessage: 'Assistant'
  },
  assistantLanguage: {
    id: 'pages.settings.Settings.assistantLanguage',
    defaultMessage: 'Assistant language'
  },
  dialogConfirmationModalLabel: {
    id: 'pages.settings.Settings.dialogConfirmationModalLabel',
    defaultMessage: 'dialog confirmation modal'
  },
  dialogButtonLabel: {
    id: 'pages.settings.Settings.dialogButtonLabel',
    defaultMessage: 'toggle dialog'
  },
  dialogHeader: {
    id: 'pages.settings.Settings.dialogHeader',
    defaultMessage: 'Dialog'
  },
  dialogDescription: {
    id: 'pages.settings.Settings.dialogDescription',
    defaultMessage:
      'Activate dialog if you are migrating an existing dialog skill. <strong>When you activate the dialog feature, it takes precedence over actions.</strong> You can use actions to supplement a dialog-based conversation. Learn more about <a>migrating dialog skills</a> and <a2>calling actions from a dialog</a2>.'
  },
  dialogSwitchHeader: {
    id: 'pages.settings.Settings.dialogSwitchHeader',
    defaultMessage: 'Switch to classic {productName} experience'
  },
  dialogSwitchButtonLabel: {
    id: 'pages.settings.Settings.dialogSwitchButtonLabel',
    defaultMessage: 'Switch to classic'
  },
  activateDialog: {
    id: 'pages.settings.Settings.activateDialog',
    defaultMessage: 'Activate dialog'
  },
  deactivateDialog: {
    id: 'pages.settings.Settings.deactivateDialog',
    defaultMessage: 'Deactivate dialog'
  },
  activateModalHeading: {
    id: 'pages.settings.Settings.activateModalHeading',
    defaultMessage: 'Use dialog and actions together'
  },
  activateModalBody: {
    id: 'pages.settings.Settings.activateModalBody',
    defaultMessage:
      'Activate dialog so you can migrate an existing dialog skill. <strong>When you activate the dialog feature, it takes precedence over any actions you build.</strong> Dialog becomes the primary conversation for your assistant.'
  },
  deactivateModalBody: {
    id: 'pages.settings.Settings.deactivateModalBody',
    defaultMessage:
      'Deactivate dialog if you want to resume using actions as the primary conversation. The dialog editor will be removed from the user interface, but dialog content won’t be deleted. You can reactivate dialog at any time.'
  },
  disableDialogButtonLabel: {
    id: 'pages.settings.Settings.disableDialogButtonLabel',
    defaultMessage: 'Disable dialog'
  },
  deactivatedDialogSuccess: {
    id: 'pages.settings.Settings.deactivatedDialogSuccess',
    defaultMessage: 'Dialog has been deactivated in your assistant'
  },
  activatedDialogSuccess: {
    id: 'pages.settings.Settings.activatedDialogSuccess',
    defaultMessage: 'Dialog has been activated in your assistant'
  },
  multilingualIsInactiveHeader: {
    id: 'pages.settings.Settings.multilingualIsInactiveHeader',
    defaultMessage: 'Download/Upload'
  },
  multilingualIsActiveHeader: {
    id: 'pages.settings.Settings.multilingualIsActiveHeader',
    defaultMessage: 'Export your assistant data'
  },
  multilingualDescription: {
    id: 'pages.settings.Settings.multilingualDescription',
    defaultMessage:
      'Download or upload your assistant as a ZIP file. You can also enable the download of language data files so you can translate training examples and assistant responses into other languages and use in additional assistants. <a>Learn more</a>'
  },
  loadingMultilingual: {
    id: 'pages.settings.Settings.loadingMultilingual',
    defaultMessage: 'Activation in progress'
  },
  downloadUploadButtonLabel: {
    id: 'pages.settings.Settings.downloadUploadButtonLabel',
    defaultMessage: 'Download/Upload files'
  },
  multilingualDownloadButtonLabel: {
    id: 'pages.settings.Settings.multilingualDownloadButtonLabel',
    defaultMessage: 'Enable multilingual download'
  },
  multilingualTagLabel: {
    id: 'pages.settings.Settings.multilingualTagLabel',
    defaultMessage: 'Multilingual enabled'
  },
  downloadAndUploadModalTitle: {
    id: 'pages.settings.Settings.downloadAndUploadModalTitle',
    defaultMessage: 'Download/Upload'
  },
  downloadLabel: {
    id: 'pages.settings.Settings.downloadLabel',
    defaultMessage: 'Download'
  },
  uploadLabel: {
    id: 'pages.settings.Settings.uploadLabel',
    defaultMessage: 'Upload'
  },
  deleteLabel: {
    id: 'pages.settings.Settings.deleteLabel',
    defaultMessage: 'Delete'
  },
  downloadUploadAssistantFiles: {
    id: 'pages.settings.Settings.downloadUploadAssistantFiles',
    defaultMessage: 'Download or upload assistant files'
  },
  downloadType: {
    id: 'pages.settings.Settings.downloadType',
    defaultMessage: 'Download type'
  },
  uploadType: {
    id: 'pages.settings.Settings.uploadType',
    defaultMessage: 'Upload type'
  },
  assistantOnlyRadioButtonText: {
    id: 'pages.settings.Settings.assistantOnlyRadioButtonText',
    defaultMessage: 'Assistant only'
  },
  multilingualFilePackageButtonText: {
    id: 'pages.settings.Settings.multilingualFilePackageButtonText',
    defaultMessage: 'Multilingual file package'
  },
  downloadWithMultilingualDescription: {
    id: 'pages.settings.Settings.downloadWithMultilingualDescription',
    defaultMessage:
      'Download a ZIP file that includes:{br}{br}Data folder <span>(Do not edit)</span>{br}CSV of training examples{br}CSV of assistant responses'
  },
  downloadWithoutMultilingualDescription: {
    id: 'pages.settings.Settings.downloadWithoutMultilingualDescription',
    defaultMessage:
      'Download a ZIP file that includes your assistant as a JSON file'
  },
  invalidVersionText: {
    id: 'pages.settings.Settings.invalidVersionText',
    defaultMessage: 'You have not created any versions yet.'
  },
  selectVersionToDownload: {
    id: 'pages.settings.Settings.selectVersionToDownload',
    defaultMessage: 'Please select a version to download'
  },
  chooseVersion: {
    id: 'pages.settings.Settings.chooseVersion',
    defaultMessage: 'Choose version'
  },
  uploadWithoutMultilingualDescription: {
    id: 'pages.settings.Settings.uploadWithoutMultilingualDescription',
    defaultMessage:
      'Upload a ZIP file that includes your assistant as a JSON file.{br}{br}{isWxoEnabled, select, true {} other {If the file contains dialog content, you need to activate dialog in your assistant before uploading.}}'
  },
  uploadWithMultilingualDescription: {
    id: 'pages.settings.Settings.uploadWithMultilingualDescription',
    defaultMessage:
      'Upload a ZIP file that includes:{br}{br}Data folder <span>(Do not edit)</span>{br}CSV file of training examples{br}CSV file of assistant responses{br}{br}{isWxoEnabled, select, true {} other {If the file contains dialog content, you need to activate dialog in your assistant before uploading.}}'
  },
  uploadContainerText: {
    id: 'pages.settings.Settings.uploadContainerText',
    defaultMessage: 'Drag and drop file here or click to select a file'
  },
  uploadNotificationTitle: {
    id: 'pages.settings.Settings.uploadNotificationTitle',
    defaultMessage: 'Uploaded data will override your assistant'
  },
  uploadNotificationSubtitle: {
    id: 'pages.settings.Settings.uploadNotificationSubtitle',
    defaultMessage: 'Upload to a new assistant to preserve your data.'
  },
  removeFile: {
    id: 'pages.settings.Settings.removeFile',
    defaultMessage: 'Remove file'
  },
  warningTitle: {
    id: 'pages.settings.Settings.warningTitle',
    defaultMessage: 'This will replace your existing draft content.'
  },
  warningDescription: {
    id: 'pages.settings.Settings.warningDescription',
    defaultMessage:
      'You can download your existing actions content before uploading a new ZIP file.'
  },
  uploadAndReplace: {
    id: 'pages.settings.Settings.uploadAndReplace',
    defaultMessage: 'Upload and replace'
  },
  downloadInProgress: {
    id: 'pages.settings.Settings.downloadInProgress',
    defaultMessage: 'Download in progress'
  },
  uploadInProgress: {
    id: 'pages.settings.Settings.uploadInProgress',
    defaultMessage: 'Upload in progress'
  },
  uploadInlineError: {
    id: 'pages.settings.Settings.uploadInlineError',
    defaultMessage: 'There are errors with your file upload'
  },
  noUploadCertificate: {
    id: 'pages.settings.Settings.noUploadCertificate',
    defaultMessage: 'Certificates not uploaded!'
  },
  noUploadCertificateDescription: {
    id: 'pages.settings.Settings.noUploadCertificateDescription',
    defaultMessage:
      'The service integrations and extensions may fail if you do not upload certificates.'
  },
  seeDetails: {
    id: 'pages.settings.Settings.seeDetails',
    defaultMessage: 'See details'
  },
  uploadErrorModelHeader: {
    id: 'pages.settings.Settings.uploadErrorModelHeader',
    defaultMessage: 'There are errors with your file upload'
  },
  uploadErrorModelSubHeader: {
    id: 'pages.settings.Settings.uploadErrorModelSubHeader',
    defaultMessage: 'Please update your files and try again.'
  },
  uploadErrorPlusMoreText: {
    id: 'pages.settings.Settings.uploadErrorPlusMoreText',
    defaultMessage: '+{number} more'
  },
  deleteHeader: {
    id: 'pages.settings.Settings.deleteHeader',
    defaultMessage: 'Delete this assistant'
  },
  deleteDescription: {
    id: 'pages.settings.Settings.deleteDescription',
    defaultMessage:
      "This action can't be undone. Any integrations that are configured for the assistant will also be deleted."
  },
  deleteButtonLabel: {
    id: 'pages.settings.Settings.deleteButtonLabel',
    defaultMessage: 'Delete assistant'
  },
  saveEditsBody: {
    id: 'pages.settings.Settings.saveEditsBody',
    defaultMessage:
      'Would you like to save your edits to your settings before you leave?'
  },
  viewDetails: {
    id: 'pages.settings.Settings.viewDetails',
    defaultMessage: 'View details'
  },
  trustAll: {
    id: 'pages.settings.Settings.trustAll',
    defaultMessage:
      'Trust all certificates, operation insecure (not recommended).'
  },
  trustAllCertificates: {
    id: 'pages.settings.Settings.trustAllCertificates',
    defaultMessage: 'Trust all certificates'
  },
  trustAllDescription: {
    id: 'pages.settings.Settings.trustAllDescription',
    defaultMessage:
      'Do you want to update your SSL/TLS certificate configuration to trust all certificates? This will remove any existing SSL/TLS certificates that are added to your assistant.'
  },
  uploadCertificate: {
    id: 'pages.settings.Settings.uploadCertificate',
    defaultMessage: 'Upload certificate'
  },
  uploadCertificateDescription: {
    id: 'pages.settings.Settings.uploadCertificate',
    defaultMessage:
      'Choose a file to upload your trusted certificates. To upload multiple files merge the files to one before uploading.'
  },
  uploadCertificateDescription2: {
    id: 'pages.settings.Settings.uploadCertificate2',
    defaultMessage:
      'There can only be one SSL/TLS certificate recognized per assistant. Max file size is 500kb. Only PEM format is supported.'
  },
  deleteCertificate: {
    id: 'pages.settings.Settings.deleteCertificate',
    defaultMessage: 'Delete certificate'
  },
  deleteCertificateDescription: {
    id: 'pages.settings.Settings.deleteCertificateDescription',
    defaultMessage:
      'Are you sure you want to delete your SSL/TLS certificates from the assistant? This will remove any uploaded SSL/TLS certificate added to your assistant.'
  },
  acknowledgeWarning: {
    id: 'pages.settings.Settings.acknowledgeWarning',
    defaultMessage: 'I acknowledge that I read and understand this warning.'
  },
  yes: {
    id: 'pages.settings.Settings.yes',
    defaultMessage: 'Yes'
  },
  no: {
    id: 'pages.settings.Settings.no',
    defaultMessage: 'No'
  },
  generateApiKey: {
    id: 'pages.settings.Settings.generateApiKey',
    defaultMessage: 'Generate API key'
  },
  generatingApiKey: {
    id: 'pages.settings.Settings.generatingApiKey',
    defaultMessage: 'Generating...'
  },
  apiKeyModalHeading: {
    id: 'pages.settings.Settings.apiKeyModalHeading',
    defaultMessage: 'API key successfully created'
  },
  apiKeyModalBody: {
    id: 'pages.settings.Settings.apiKeyModalBody',
    defaultMessage:
      'Each API key is unique and not recoverable. You must generate a new key each time you require one.'
  },
  apiKey: {
    id: 'pages.settings.Settings.apiKey',
    defaultMessage: 'API key'
  },
  apiKeyGenericErrorMessage: {
    id: 'pages.settings.Settings.apiKey.genericErrorMessage',
    defaultMessage: 'Could not create API key.'
  },
  apiKeyLimitErrorMessage: {
    id: 'pages.settings.Settings.apiKey.limitErrorMessage',
    defaultMessage:
      'Could not create API key. Maximum number of API keys reached.'
  },
  closeButtonLabel: {
    id: 'pages.settings.Settings.closeButtonLabel',
    defaultMessage: 'Close'
  },
  copyButtonLabel: {
    id: 'pages.settings.Settings.copyButtonLabel',
    defaultMessage: 'Copy'
  }
});

export default messages;
